import { Box, HStack, Image, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Spinner, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, Text, VStack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { createMedicineEntryLog, getSimilarMedicineList } from "../../api";
import { colors } from "../../styles";
import { ISimilarMedicineList } from "../../types";
import MiniMedListItem from "../Medicine/MiniMedListItem";

interface SimilarModalProps {
    medicineId: string;
    surface: "SEARCH" | "MEDICINE";
    
    isOpen : boolean;
    onClose : () => void;
}

export default function SimilarModal(props : SimilarModalProps) {

    const { medicineId, surface } = props;

    /* 모달 목록에서 상세로 여는 클릭을 기록한다 */

    const handleOpenMedicine = (openedMedicineId: string, source: "SIMILAR_IDENTICAL" | "SIMILAR_RECOMMEND") => {

        if (medicineId && openedMedicineId) {
            createMedicineEntryLog({
                source,
                surface,
                medicineId: openedMedicineId,
                fromMedicineId: medicineId,
            });
        }

        window.open(`/medicine/${openedMedicineId}`, '_blank', 'noopener,noreferrer');
    }

    /* query */

    const { data: similarMedicineData, isLoading: isSimilarMedicineDataLoading } = useQuery<ISimilarMedicineList>({
        queryKey: ['similarMedicineList', medicineId],
        queryFn: () => getSimilarMedicineList({ medicineId: medicineId! }),
        // 모달이 열렸을 때만 쿼리가 요청되도록 수정
        enabled: !!medicineId && props.isOpen,
    });

    const baseMedicine = useMemo(() => {
        return similarMedicineData?.base;
    }, [similarMedicineData]);

    /* render */
    
    return (
        <Modal motionPreset="slideInBottom" size={"4xl"} isOpen={props.isOpen} onClose={props.onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text color={colors.blue} fontSize="lg" fontWeight="bold">추천</Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody paddingTop={5}>

                    {/* 기준 약품 정보 */}

                    <HStack width={"100%"} justifyContent={"space-between"} padding={5}>

                        {/* 상품 정보 */}

                        <HStack width={"100%"} justifyContent={"flex-start"} alignItems={"center"}>

                            {/* 상품 이미지 */}

                            {baseMedicine?.medicineImage ? (
                                
                                <Box
                                    width="120px"
                                    height="120px"
                                    minWidth="120px"
                                    minHeight="120px"
                                    maxWidth="120px"
                                    maxHeight="120px"
            
                                    border={"0.2px solid " + colors.g4}
                                    borderRadius="8px"
                                    overflow="hidden"
            
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Image 
                                        src={baseMedicine.medicineImage.split(";")[0]} 
                                        alt="miniMedicineImage" 
                                        width="100%"
                                        height="100%"
                                        objectFit={"contain"}
                                    />
                                </Box>
                            ) : (
                                <Box
                                    width="120px"
                                    height="120px"
                                    minWidth="120px"
                                    minHeight="120px"
                                    maxWidth="120px"
                                    maxHeight="120px"
            
                                    border={"0.2px solid " + colors.g4}
                                    borderRadius="8px"
                                    overflow="hidden"
            
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Image 
                                        src="/png/nutrients.png" 
                                        alt="miniMedicineImage" 
                                        width="100%"
                                        height="100%"
                                        objectFit={"contain"}
                                    />
                                </Box>
                            )}


                            {/* 상품 정보 - 상품명, 포장 단위 */}

                            <VStack 
                                justifyContent={"center"}
                                alignItems={"flex-start"}
                                marginLeft={5}
                                spacing={2}
                            >
                                <Text fontSize={14} fontWeight={700} color={colors.g3}>{baseMedicine?.seller ?? "-"}</Text>
                                <Text fontSize={16} fontWeight={700} color={colors.g1}>{baseMedicine?.nameKor?.split('(')[0].trim() ?? "-"}</Text>
                                <Text fontSize={12} fontWeight={400} color={colors.g1}>{baseMedicine?.ingredientInfo ?? "-"}</Text>
                            </VStack>
                        </HStack>
                    </HStack>

                    {/* Tabs */}

                    <Tabs variant='soft-rounded' isFitted marginTop={5}>
                        <Box padding={1} marginX={5} backgroundColor={colors.g5} borderRadius={30} justifyContent={"center"} alignItems={"center"}>
                            <TabList>
                                <Tab _selected={{color: colors.black, fontWeight: 500, backgroundColor: colors.white}} paddingX={6} paddingY={2} fontSize={16}>동일성분 동일함량</Tab>
                                <Tab _selected={{color: colors.black, fontWeight: 500, backgroundColor: colors.white}} paddingX={6} paddingY={2} fontSize={16}>유사 추천 제품</Tab>
                            </TabList>
                        </Box>
                        <TabPanels>

                            {/* 동일성분 동일함량 */}

                            <TabPanel padding={5} minHeight={"300px"}>

                                {(!isSimilarMedicineDataLoading && similarMedicineData && similarMedicineData.identical.length > 0) && (
                                    <VStack 
                                        alignItems={"flex-start"} 
                                        width={"100%"} 
                                        marginY={5} 
                                        gap={5}
                                        minHeight={"450px"}
                                        maxHeight={"450px"}
                                        overflowY="auto"
                                    >
                                        {similarMedicineData?.identical.map((medicine) => (
                                            <Stack 
                                                key={medicine.id} 
                                                width={"100%"} 
                                                onClick={() => handleOpenMedicine(medicine.id, "SIMILAR_IDENTICAL")}
                                                cursor={"pointer"}
                                            >
                                                <MiniMedListItem 
                                                    key={medicine.id} 
                                                    seller={medicine.seller}
                                                    medicineImage={medicine.medicineImage} 
                                                    medicineName={medicine.nameKor} 
                                                    packageUnit={medicine.packageUnit} 
                                                    ingredientInfo={null} 
                                                />
                                            </Stack>
                                        ))}
                                    </VStack>
                                )}

                                {(!isSimilarMedicineDataLoading && similarMedicineData && similarMedicineData.identical.length === 0) && (
                                    <Stack 
                                        width={"100%"} 
                                        justifyContent={"center"} 
                                        alignItems={"center"} 
                                        height={"450px"}
                                        padding={5}
                                    >
                                        <Text fontSize={16} fontWeight={500} color={colors.g1}>약품 목록이 없습니다.</Text>
                                    </Stack>
                                )}

                                {isSimilarMedicineDataLoading && (
                                    <Stack 
                                        width={"100%"} 
                                        justifyContent={"center"} 
                                        alignItems={"center"}
                                        height={"450px"}
                                        padding={5}
                                    >
                                        <Spinner color={colors.blue} />
                                    </Stack>
                                )}

                            </TabPanel>

                            {/* 유사 추천 제품 */}

                            <TabPanel padding={5} minHeight={"300px"}>

                                {(!isSimilarMedicineDataLoading && similarMedicineData && similarMedicineData.similar.length > 0) && (
                                    <VStack 
                                        alignItems={"flex-start"} 
                                        width={"100%"} 
                                        marginY={5} 
                                        gap={5}
                                        minHeight={"450px"}
                                        maxHeight={"450px"}
                                        overflowY="auto"
                                    >
                                        {similarMedicineData?.similar.map((medicine) => (
                                            <Stack 
                                                key={medicine.medicine.id} 
                                                width={"100%"} 
                                                onClick={() => handleOpenMedicine(medicine.medicine.id, "SIMILAR_RECOMMEND")}
                                                cursor={"pointer"}
                                            >
                                                <MiniMedListItem 
                                                    key={medicine.medicine.id} 
                                                    seller={medicine.medicine.seller}
                                                    medicineImage={medicine.medicine.medicineImage} 
                                                    medicineName={medicine.medicine.nameKor} 
                                                    packageUnit={medicine.medicine.packageUnit} 
                                                    ingredientInfo={medicine.medicine.ingredientInfo} 
                                                />
                                            </Stack>
                                        ))}
                                    </VStack>
                                )}

                                {(!isSimilarMedicineDataLoading && similarMedicineData && similarMedicineData.similar.length === 0) && (
                                    <Stack 
                                        width={"100%"} 
                                        justifyContent={"center"} 
                                        alignItems={"center"} 
                                        height={"450px"}
                                        padding={5}
                                    >
                                        <Text fontSize={16} fontWeight={500} color={colors.g1}>약품 목록이 없습니다.</Text>
                                    </Stack>
                                )}

                                {isSimilarMedicineDataLoading && (
                                    <Stack 
                                        width={"100%"} 
                                        justifyContent={"center"} 
                                        alignItems={"center"}
                                        height={"450px"}
                                        padding={5}
                                    >
                                        <Spinner color={colors.blue} />
                                    </Stack>
                                )}

                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}